import { BusinessMetricTypeId } from "@models/configuration";
import { MAAnalysisDataDTO } from "@models/managed-accounts/entities/step/fee";
import { AnalysisSelectionDTO } from "@models/platform-analysis/entities/analysis";
import { AnalysisDecisionStatus } from "@models/platform-analysis/enums/feature/decisionStatus";
import { FeatureAnalysisRowLevel } from "@models/platform-analysis/enums/feature/featureAnalysisRowLevel";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { ProductRecommendationDTO } from "./summary";

export interface ConfigurationSectionBaseDTO {
  id: string;
  name: string;
}

export interface AnalysisGetFeatureSectionsDTO extends AnalysisSelectionDTO {
  sections: AnalysisConfigurationFeatureSectionDTO[];
}

export interface AnalysisConfigurationFeatureSectionDTO
  extends ConfigurationSectionBaseDTO {
  configurationSectionId?: string;
  configurationGroupId?: string;
  description?: string;
  order: number;
  iconUrl?: string | null;
  iconS3Key?: string | null;
  totalFeature: number;
  totalSelectedFeature: number;
  configurationVersion?: number;
  isSelected: boolean;
  subSections: AnalysisConfigurationFeatureSubSectionDTO[];
  staticId?: string | null;
}

export interface AnalysisConfigurationFeatureSubSectionDTO
  extends AnalysisConfigurationFeatureSectionDTO {
  parentSectionId?: string;
  features?: AnalysisConfigurationFeatureDTO[];
}

export interface AnalysisConfigurationFeatureDTO {
  id: string;
  name: string;
  description?: string;
  isSelected: boolean;
  isEssential: boolean;
  order: number;
  configurationSectionId?: string;
}

export enum PlatformAnalysisFilter {
  Common = "Common",
  Differentiators = "Differentiators",
}

export interface AnalysisFeatureDTO {
  all?: AnalysisFeatureAllDataDTO;
  shortlisted?: AnalysisFeatureAllDataDTO;
  disqualified?: AnalysisFeatureAllDataDTO;

  /** @deprecated Property removed on merged PA/SR */
  selectedProducts: SelectedProductDTO[];
  /** @deprecated Property removed on merged PA/SR */
  overallScore: OverallScoreDTO;
  /** @deprecated Property removed on merged PA/SR */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sections: any[]; // AnalysisStepFeatureDTO[]
}

export interface AnalysisFeatureAllDataDTO {
  selectedProducts: SelectedProductDTO[];
  overallScore: OverallScoreDTO;
  sections: AnalysisSectionDTO[];
}

export interface SelectedProductDTO<TDecisionStatus = AnalysisDecisionStatus> {
  id: string;
  productName?: string;
  subProductName?: string;
  productLogo?: ImageDTO;
  totalScore: number;
  isProductMain: boolean;
  order: number;
  comment?: AnalysisCommentDTO;
  isHidden?: boolean;
  decisionStatus?: TDecisionStatus | null;
  previousDecisionStatus?: TDecisionStatus | null;
  isMissingEssential?: boolean;

  // internal use only
  recommendation?: Partial<ProductRecommendationDTO>;
}

export interface OverallScoreDTO {
  totalFeature?: number;
  totalSelectedFeature?: number;
  analysisData: AnalysisDataDTO[];
}

export interface AnalysisSectionDTO {
  id?: string;
  name?: string;
  description?: string;
  iconUrl?: string;
  order: number;
  totalFeature: number;
  totalSelectedFeature: number;
  analysisData?: AnalysisDataDTO[];
  subSections?: AnalysisSectionDTO[];
  features?: AnalysisFeatureDTO[];
  businessMetricTypeId?: BusinessMetricTypeId;
  businessMetricTypeName?: string;
}

export interface AnalysisFeatureDTO {
  id: string;
  name: string;
  description?: string | null;
  order: number;
  analysisData: AnalysisDataDTO[];
  isEssential?: boolean;
}
export interface AnalysisDataDTO {
  productId: string;
  order: number;
  totalScore?: number;
  value?: string;
  imageGallery?: ImageDTO[];
  isPositiveScore?: boolean;
  fieldTypeId?: string;
  description?: string;
  isMissingEssential?: boolean;
}

export interface AnalysisCommentDTO {
  id: string | null;
  comment: string;
}

export interface ImageDTO {
  key: string;
  url: string;
}

export class AnalysisFeatureWithUniqueDTO {
  featureId = "";
  featureName = "";
  uniqueFeature = false;
  isEssential?: boolean = false;
}
export class AnalysisFeatureIncludeDTO {
  sectionId = "";
  sectionName = "";
  subSectionId = "";
  subSectionName = "";
  features: AnalysisFeatureWithUniqueDTO[] = [];
}

export class FeatureAnalysisRowDTO {
  level: FeatureAnalysisRowLevel = FeatureAnalysisRowLevel.Section;
  isOverallScoreRow?: boolean = false;
  id = "";
  name = "";
  longestDataName?: string;
  subRows?: FeatureAnalysisRowDTO[] = [];
  analysisData: AnalysisDataDTO[] = [];
  description?: string;
  isEssential?: boolean;
  iconUrl?: string;
  allFeatureAvailable?: boolean;
  businessMetricTypeId?: BusinessMetricTypeId;
  variableName?: AnalysisVariable;
  MAAnalysisData?: MAAnalysisDataDTO[];
  totalFeatures?: number = 0;
  totalFeaturesSelected?: number = 0;
}
